import { memo } from 'react';
import { Radio, RadioGroup } from '@fluentui/react-components';
import { useAppStore } from '@/stores/appStore';
import { useWorkflowViewPrefsStore } from '@/stores/workflowViewPrefsStore';
import type { LayoutOrientation } from '@/stores/workflowStore';
import { useSettingCardStyles } from './sectionStyles';

/**
 * Appearance preferences: theme and default canvas orientation.
 *
 * Reduce motion lives in Accessibility — that's where every operating
 * system surfaces it, and where users look for it.
 *
 * The default canvas orientation lives in `workflowViewPrefsStore` —
 * that store also persists per-workflow overrides, so this one control
 * stays in sync with whatever the Designer toolbar surfaces.
 */
export const AppearanceSection = memo(() => {
  const styles = useSettingCardStyles();
  // Subscribe per-field so unrelated app-store changes (tenant/project/app
  // navigation, recent-work updates) don't re-render this section.
  const theme = useAppStore((s) => s.theme);
  const setTheme = useAppStore((s) => s.setTheme);
  const defaultCanvasOrientation = useWorkflowViewPrefsStore((s) => s.defaults.orientation);
  const setDefaultCanvasOrientation = useWorkflowViewPrefsStore((s) => s.setDefault);

  return (
    <div className={styles.list}>
      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Theme</p>
          <p className={styles.cardHelp}>
            Switch between light and dark palettes. A high-contrast variant is planned.
          </p>
        </div>
        <div className={styles.cardControl}>
          <RadioGroup
            layout="horizontal"
            value={theme}
            onChange={(_, data) => setTheme(data.value as 'light' | 'dark')}
            aria-label="Theme"
          >
            <Radio value="light" label="Light" />
            <Radio value="dark" label="Dark" />
          </RadioGroup>
        </div>
      </div>

      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Default canvas orientation</p>
          <p className={styles.cardHelp}>
            Applied when you open <strong>any</strong> new workflow. You can still flip an individual workflow
            with the canvas toolbar — that does not change this preference.
          </p>
          <div className={styles.cardBody}>
            <RadioGroup
              layout="horizontal"
              value={defaultCanvasOrientation}
              onChange={(_, data) => setDefaultCanvasOrientation(data.value as LayoutOrientation)}
              aria-label="Default canvas orientation"
            >
              <Radio value="horizontal" label="Horizontal (left → right)" />
              <Radio value="vertical" label="Vertical (top → bottom)" />
            </RadioGroup>
          </div>
        </div>
      </div>
    </div>
  );
});
AppearanceSection.displayName = 'AppearanceSection';
